import React from 'react';
import { css, cx } from '@emotion/css';
import { Theme, useTheme, Typography, Tooltip } from '@mui/material';
import { BiDesktop, BiMobileAlt } from 'react-icons/bi';

export interface AccessData {
  portalAccess: boolean;
  mobileAccess: boolean;
}

interface AccessTypeStepProps {
  data: AccessData;
  onChange: (data: AccessData) => void;
  error?: string;
  canEnableMobile: boolean;
}

const AccessTypeStep: React.FC<AccessTypeStepProps> = ({ data, onChange, error, canEnableMobile }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const togglePortal = () => onChange({ ...data, portalAccess: !data.portalAccess });
  const toggleMobile = () => {
    if (!canEnableMobile) return;
    onChange({ ...data, mobileAccess: !data.mobileAccess });
  };

  const mobileItem = (
    <div
      className={cx(
        cls.typeItem,
        data.mobileAccess && canEnableMobile ? cls.typeItemActive : null,
        !canEnableMobile ? cls.typeItemDisabled : null,
      )}
      onClick={toggleMobile}
    >
      <BiMobileAlt size={28} />
      <Typography variant="h5" sx={{ marginBottom: '6px', fontWeight: 600 }}>
        Mobile App Access
      </Typography>
      <Typography variant="body2" sx={{ textWrap: 'pretty', marginBottom: '6px' }}>
        Access the HopDrive Mobile App to perform these moves
      </Typography>
    </div>
  );

  return (
    <div>
      <Typography variant="overline" fontWeight={600} color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        What access does this person need?
      </Typography>

      <div className={cls.type}>
        <div
          className={cx(cls.typeItem, data.portalAccess ? cls.typeItemActive : null)}
          onClick={togglePortal}
        >
          <BiDesktop size={28} />
          <Typography variant="h5" sx={{ marginBottom: '6px', fontWeight: 600 }}>
            HopDrive Portal Access
          </Typography>
          <Typography variant="body2" sx={{ textWrap: 'pretty', marginBottom: '6px' }}>
            Access the portal to manage users, create moves, and other features
          </Typography>
        </div>

        {canEnableMobile ? (
          mobileItem
        ) : (
          <Tooltip title="Mobile access requires a rooftop that is Lite or has Mobile Service enabled" arrow>
            {mobileItem}
          </Tooltip>
        )}
      </div>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </div>
  );
};

const useStyles = (theme?: Theme) => ({
  type: css`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  `,
  typeItem: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-width: 212px;
    max-width: 424px;
    padding: 12px;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    text-align: center;
    background-color: ${theme?.palette?.action?.hover};
    color: ${theme?.palette?.text?.primary};
    cursor: pointer;
    transition: 0.1s ease-in-out;
    :hover,
    :focus {
      background-color: ${theme?.palette?.action?.selected};
    }
    :active {
      background-color: ${theme?.palette?.action?.disabledBackground};
    }
  `,
  typeItemActive: css`
    border: 1px solid ${theme?.palette?.primary?.main};
    background-color: ${theme?.palette?.primary?.main};
    color: ${theme?.palette?.primary?.contrastText};
    :hover,
    :focus {
      background-color: ${theme?.palette?.primary?.main};
    }
    :active {
      background-color: ${theme?.palette?.primary?.main};
    }
  `,
  typeItemDisabled: css`
    opacity: 0.5;
    cursor: not-allowed;
    :hover,
    :focus {
      background-color: ${theme?.palette?.action?.hover};
    }
    :active {
      background-color: ${theme?.palette?.action?.hover};
    }
  `,
});

export default AccessTypeStep;
